import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

// Color constants
const COLORS = {
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
};

// Default 5-segment colors (red to green, left to right)
const DEFAULT_COLORS = [
  COLORS.red,
  COLORS.orange,
  COLORS.yellow,
  COLORS.lime,
  COLORS.green,
];

// Symmetric 7-segment colors for pH/ORP (green center, red edges)
const SYMMETRIC_7_COLORS = [
  COLORS.red,
  COLORS.orange,
  COLORS.yellow,
  COLORS.green,
  COLORS.yellow,
  COLORS.orange,
  COLORS.red,
];

// Calculate segment color based on its position relative to optimal range
function getSegmentColor(segmentIndex, totalSegments, optimalStartSegment, optimalEndSegment) {
  // If segment is within optimal range
  if (segmentIndex >= optimalStartSegment && segmentIndex <= optimalEndSegment) {
    return COLORS.green;
  }

  // Distance from optimal range (in segments)
  const distanceFromOptimal = segmentIndex < optimalStartSegment
    ? optimalStartSegment - segmentIndex
    : segmentIndex - optimalEndSegment;

  // Color based on distance: closer = yellow/lime, farther = orange/red
  if (distanceFromOptimal === 1) {
    return COLORS.lime;
  } else if (distanceFromOptimal === 2) {
    return COLORS.yellow;
  } else if (distanceFromOptimal === 3) {
    return COLORS.orange;
  }
  return COLORS.red;
}

function GaugeMeter({
  value,
  min,
  max,
  optimalMin,
  optimalMax,
  segments = 5,
  size = 140,
  thickness = 18,
  colors = null, // If provided, use these colors; otherwise calculate from optimal range
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);

  const centerX = size / 2;
  const centerY = size / 2 + 10;
  const radius = (size - thickness) / 2 - 10;

  const startAngle = Math.PI;
  const endAngle = 2 * Math.PI;
  const totalAngle = endAngle - startAngle;
  const gapAngle = 0.08;
  const segmentAngle = (totalAngle - (gapAngle * (segments - 1))) / segments;

  const needleAngle = startAngle + (normalizedValue * totalAngle);
  const needleLength = radius - 5;
  const needleX = centerX + Math.cos(needleAngle) * needleLength;
  const needleY = centerY + Math.sin(needleAngle) * needleLength;

  // Calculate which segments the optimal range covers
  const optimalStartNormalized = optimalMin !== undefined ? (optimalMin - min) / (max - min) : 0.4;
  const optimalEndNormalized = optimalMax !== undefined ? (optimalMax - min) / (max - min) : 0.6;

  // Map normalized positions to segment indices
  const optimalStartSegment = Math.floor(optimalStartNormalized * segments);
  const optimalEndSegment = Math.min(Math.floor(optimalEndNormalized * segments), segments - 1);

  // Create pill-shaped arc segment with rounded ends
  const createPillArcPath = (startAng, endAng, r, t) => {
    const innerR = r - t / 2;
    const outerR = r + t / 2;
    const capR = t / 2;

    const ox1 = centerX + Math.cos(startAng) * outerR;
    const oy1 = centerY + Math.sin(startAng) * outerR;
    const ox2 = centerX + Math.cos(endAng) * outerR;
    const oy2 = centerY + Math.sin(endAng) * outerR;

    const ix1 = centerX + Math.cos(startAng) * innerR;
    const iy1 = centerY + Math.sin(startAng) * innerR;
    const ix2 = centerX + Math.cos(endAng) * innerR;
    const iy2 = centerY + Math.sin(endAng) * innerR;

    const largeArc = endAng - startAng > Math.PI ? 1 : 0;

    return [
      `M ${ox1} ${oy1}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${ox2} ${oy2}`,
      `A ${capR} ${capR} 0 0 1 ${ix2} ${iy2}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${ix1} ${iy1}`,
      `A ${capR} ${capR} 0 0 1 ${ox1} ${oy1}`,
      'Z'
    ].join(' ');
  };

  const segmentPaths = [];
  for (let i = 0; i < segments; i++) {
    const segStart = startAngle + i * (segmentAngle + gapAngle);
    const segEnd = segStart + segmentAngle;

    // Use provided colors if available, otherwise calculate based on optimal range
    const segmentColor = colors
      ? (colors[i] || colors[i % colors.length])
      : getSegmentColor(i, segments, optimalStartSegment, optimalEndSegment);

    segmentPaths.push({
      path: createPillArcPath(segStart, segEnd, radius, thickness),
      color: segmentColor,
    });
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: size / 2 + 30,
    }}>
      <svg width={size} height={size / 2 + 30} viewBox={`0 0 ${size} ${size / 2 + 30}`}>
        {segmentPaths.map((segment, index) => (
          <path
            key={index}
            d={segment.path}
            fill={segment.color}
            style={{
              filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))',
            }}
          />
        ))}

        {/* Center hub */}
        <circle
          cx={centerX}
          cy={centerY}
          r={8}
          fill="#374151"
          stroke="#1f2937"
          strokeWidth={2}
        />

        {/* Needle */}
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#e5e7eb"
          strokeWidth={3}
          strokeLinecap="round"
          style={{
            filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.4))',
          }}
        />

        {/* Needle center cap */}
        <circle
          cx={centerX}
          cy={centerY}
          r={5}
          fill="#6b7280"
        />
      </svg>
    </Box>
  );
}

// Export color presets for easy use
GaugeMeter.COLOR_PRESETS = {
  DEFAULT: DEFAULT_COLORS,
  SYMMETRIC_7: SYMMETRIC_7_COLORS,
};

GaugeMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  segments: PropTypes.number,
  size: PropTypes.number,
  thickness: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
};

export default GaugeMeter;
